import { useEffect, useState } from 'react';

/**
 * Hook to handle mobile viewport height and keyboard detection.
 * 
 * Strategy: We do NOT resize the layout when keyboard opens. Instead:
 * - We detect keyboard state and provide it to components
 * - We disable animations/transitions during keyboard focus
 * - We let iOS handle scroll-into-view natively
 * 
 * The `--app-height` is set ONCE on mount and only updated on orientation change,
 * NOT on keyboard open/close.
 */
export default function useMobileViewport() {
    const [keyboardOpen, setKeyboardOpen] = useState(false);
    const [viewportHeight, setViewportHeight] = useState(() => window.innerHeight);

    useEffect(() => {
        // Set initial height (full viewport, not visual viewport)
        const setInitialHeight = () => {
            const h = window.innerHeight;
            setViewportHeight(h);
            document.documentElement.style.setProperty('--app-height', `${h}px`);
        };

        // Detect keyboard based on visual viewport vs window height
        const detectKeyboard = () => {
            const vv = window.visualViewport;
            if (!vv) return;

            const windowH = window.innerHeight;
            const visualH = Math.round(vv.height);
            const diff = windowH - visualH;
            const isKeyboard = diff > 150;

            setKeyboardOpen(isKeyboard);

            if (isKeyboard) {
                document.body.classList.add('keyboard-open');
                document.documentElement.style.setProperty('--keyboard-height', `${diff}px`);
            } else {
                document.body.classList.remove('keyboard-open');
                document.documentElement.style.setProperty('--keyboard-height', '0px');
            }
        };

        // Handle focus: immediately enable keyboard mode visually
        const handleFocusIn = (e) => {
            const target = e.target;
            const isInput = target && (
                target.tagName === 'INPUT' ||
                target.tagName === 'TEXTAREA' ||
                target.isContentEditable
            );

            if (isInput) {
                // Immediately add keyboard class to disable transitions
                document.body.classList.add('keyboard-open');
            }
        };

        // Handle blur: wait a bit then check if keyboard is really closed
        const handleFocusOut = () => {
            setTimeout(() => {
                // Only remove if no other input is focused
                const active = document.activeElement;
                const isStillFocused = active && (
                    active.tagName === 'INPUT' ||
                    active.tagName === 'TEXTAREA' ||
                    active.isContentEditable
                );

                if (!isStillFocused) {
                    document.body.classList.remove('keyboard-open');
                    document.documentElement.style.setProperty('--keyboard-height', '0px');
                    setKeyboardOpen(false);
                }
            }, 100);
        };

        // Handle orientation change - this is when we need to update app height
        const handleOrientationChange = () => {
            setTimeout(() => {
                setInitialHeight();
            }, 100);
        };

        // Set up listeners
        setInitialHeight();

        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', detectKeyboard);
        }

        window.addEventListener('resize', setInitialHeight);
        window.addEventListener('orientationchange', handleOrientationChange);
        window.addEventListener('focusin', handleFocusIn);
        window.addEventListener('focusout', handleFocusOut);

        return () => {
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', detectKeyboard);
            }
            window.removeEventListener('resize', setInitialHeight);
            window.removeEventListener('orientationchange', handleOrientationChange);
            window.removeEventListener('focusin', handleFocusIn);
            window.removeEventListener('focusout', handleFocusOut);
        };
    }, []);

    return { viewportHeight, keyboardOpen };
}
